import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { Tooltip } from '@neo/primitives/tooltip'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { Handle, Position, useNodesData } from '@xyflow/react'
import {
  AlertTriangle,
  BrainCircuit as Brain,
  Image,
  Lock,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Music,
  Phone,
  Send,
  Slack,
  Sparkles,
} from 'lucide-react'
import type React from 'react'
import type { PropsWithChildren } from 'react'
import { useParams } from 'router-v6'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { AgentChannels } from 'v2/types'
import { EvalBadge } from '../components/eval/eval-badge'
import { EvalStatus } from '../components/eval/types'
import { FLOW_CLASSNAMES } from '../config/base.config'
import { type FlowNodeConfig, FlowNodeIconClass, panelIconClass } from '../config/panel.config'
import { FlowNodeDescriptions } from '../constants'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { RunType } from '../run-info/store'
import { useFlowRunStore, useFlowStore } from '../store'
import type { AiAgentV2NodeData, AiSkillsNodeData, NodeDataBase } from '../types'
import { FlowNodeTypes } from '../types'
import { isAiAgentV2Type, isV210Flow } from '../utils/ai-agent-v29'
import { subAccountLockedNodeReason } from '../utils/sub-account-locks'
import { NodeMetaDebug } from './node-meta-debug'
import { NodeOutputHandles } from './node-output-handles'

interface BaseFlowNodeProps extends PropsWithChildren {
  id: string
  name: string
  description?: string
  meta: NodeDataBase['meta']
}

// Channel-specific icon mapping for AI Agent V2
const AI_AGENT_CHANNEL_ICON_MAP: Record<AgentChannels, FlowNodeConfig> = {
  [AgentChannels.whatsapp]: {
    class: panelIconClass.whatsapp,
    icon: <Send size={16} className="text-brand-text" />,
  },
  [AgentChannels.chat]: {
    class: panelIconClass.chat,
    icon: <MessageCircle className="h-4 w-4" />,
  },
  [AgentChannels.call]: {
    class: panelIconClass.call,
    icon: <Phone className="h-4 w-4" />,
  },
  [AgentChannels.message]: {
    class: panelIconClass.message,
    icon: <MessageSquare className="h-4 w-4" />,
  },
  [AgentChannels.slack]: {
    class: panelIconClass.logic,
    icon: <Slack className="text-brand-text h-4 w-4" />,
  },
}

const AI_SKILLS_TOOL_ICON_MAP = {
  image_generation: <Image className="text-brand-text h-4 w-4" />,
  image_analysis: <Image className="text-brand-text h-4 w-4" />,
  audio_transcription: <Music className="text-brand-text h-4 w-4" />,
  text_to_speech: <Music className="text-brand-text h-4 w-4" />,
}

// Helper function to format validation error messages for tooltip
const formatValidationErrors = (issues: NodeDataBase['meta']['issues']): string => {
  if (!issues || issues.length === 0) return ''

  return issues.map((issue, index) => `${index + 1}. ${issue.message}`).join('\n')
}

// Helper function to get icon configuration for a node
const getNodeIconConfig = (
  type: FlowNodeTypes,
  nodeData?: AiAgentV2NodeData | AiSkillsNodeData,
): FlowNodeConfig => {
  // For AI Agent V2, use channel-specific icons
  if (isAiAgentV2Type(type) && nodeData?.config && 'channel' in nodeData.config) {
    return (
      AI_AGENT_CHANNEL_ICON_MAP[nodeData.config.channel] || {
        class: panelIconClass.functions,
        icon: <Sparkles className="h-4 w-4" />,
      }
    )
  }
  if (type === FlowNodeTypes.ai_skills && nodeData?.config && 'tool_type' in nodeData.config) {
    return {
      class: panelIconClass.ai,
      icon: AI_SKILLS_TOOL_ICON_MAP[
        nodeData.config.tool_type as keyof typeof AI_SKILLS_TOOL_ICON_MAP
      ] || <Brain className="text-brand-text h-4 w-4" />,
    }
  }

  // For all other node types, use the default configuration
  return FlowNodeIconClass[type] || FlowNodeIconClass[FlowNodeTypes.start]
}

export const BaseFlowNode: React.FC<BaseFlowNodeProps> = ({ id, name, description, meta }) => {
  const { isDebuggingMode } = useFeatureFlags()
  const selectedID = useFlowStore((state) => state.selectedNodeID)
  const glowingID = useFlowStore((state) => state.glowingNodeID)
  const runType = useFlowRunStore((state) => state.runType)
  const isSelected = selectedID === id
  const isGlowing = glowingID === id
  const type = useNodesData(id)?.type || FlowNodeTypes.start
  const queryClient = useQueryClient()
  const { id: flowId, flowId: runFlowId } = useParams()
  const smart = type === FlowNodeTypes.ai_agent_v2 && isV210Flow(queryClient, flowId ?? runFlowId)
  // Read the node from the React Flow provider context (not the global store's
  // flowInstance, which only ever points at the editable canvas), so a read-only version
  // preview reflects the hydrated topology. NOTE: there is a single shared provider
  // (agent-builder.tsx wraps canvas + vibe agent in one) — `useReactFlow()` throws outside
  // it, so any future isolated render (storybook / direct test) must supply a provider.
  const node = useTypedReactFlow().getNode(id)
  const { shouldHighlight, evalStatus, evalPassedCount, evalTotalCount } = meta
  const nodeDescription = description
  const hasIssues = meta.issues && meta.issues.length > 0
  const lockedReason = subAccountLockedNodeReason(type)

  const iconAndClass = getNodeIconConfig(
    type as FlowNodeTypes,
    node?.data as AiAgentV2NodeData | AiSkillsNodeData,
  )

  return (
    <div
      className={cn(
        'flow-node group relative rounded-2xl transition-colors p-[1.2px] node-hover-gradient',
        isSelected && 'selected',
        isGlowing && 'node-currently-executing',
        shouldHighlight && 'node-currently-executing',
        hasIssues && 'node-has-issues',
      )}
      data-hover="true"
      data-testid={`node-${id}`}
    >
      <Handle
        position={Position.Top}
        type="target"
        className="rounded-none w-fit absolute left-1/2 -top-1 -translate-x-1/2 bg-transparent z-[100]"
        id={`${id}-target-top`}
      >
        <div className="size-1.5 rounded-full bg-tertiary pointer-events-none absolute left-1/2 top-0 -translate-x-1/2" />
      </Handle>
      <div
        className={cn(
          'relative flex flex-col gap-1 w-[236px] bg-background z-10 transition-colors border rounded-2xl hover:border-transparent',
          hasIssues && FLOW_CLASSNAMES.nodeError,
          (isSelected || shouldHighlight) && !hasIssues && 'border-transparent',
          evalStatus === EvalStatus.Passed && 'border-status-success',
          evalStatus === EvalStatus.Failed && 'border-status-danger',
          evalStatus === EvalStatus.Partial && 'border-status-warning',
          !evalStatus && 'border-st2',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-1 w-full bg-background rounded-2xl',
            runType === RunType.FlowRunLogs && !shouldHighlight && 'opacity-50',
          )}
        >
          <div className="min-h-8 p-3 rounded-xl text-s-500">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-bg2 pr-3 rounded-md overflow-hidden">
                <div className={`flex items-center p-1 ${iconAndClass.class}`}>
                  {iconAndClass.icon}
                </div>
                <div className="flex items-center gap-1">
                  <TruncatedTextWithTooltip tooltipContent={name} className="font-inter text-p-500">
                    {name}
                  </TruncatedTextWithTooltip>
                </div>
              </div>

              <div className="ml-auto flex items-center gap-0.5">
                {/* Never in embed: the inspector exposes the compiled task graph. */}
                {isDebuggingMode &&
                  !isEmbedMode() &&
                  isAiAgentV2Type(type) &&
                  !!node?.data?._meta && <NodeMetaDebug nodeId={id} nodeName={name} />}
                {lockedReason && (
                  <Tooltip content={lockedReason} delayDuration={300}>
                    <div
                      className="flex items-center p-1 text-tertiary"
                      data-testid="icon-node-subaccount-locked"
                    >
                      <Lock size={14} aria-hidden />
                      <span className="sr-only">{lockedReason}</span>
                    </div>
                  </Tooltip>
                )}
                <div className="nodrag">
                  <Button
                    variant="ghost"
                    size="iconXs"
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const rect = e.currentTarget.getBoundingClientRect()
                      const customEvent = new MouseEvent('contextmenu', {
                        bubbles: true,
                        cancelable: true,
                        clientX: rect.left + 20,
                        clientY: rect.bottom,
                      })
                      e.currentTarget.dispatchEvent(customEvent)
                    }}
                  >
                    <MoreVertical size={16} className="text-secondary" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="font-inter text-s-400 text-secondary mt-2">
              {nodeDescription || FlowNodeDescriptions[type as FlowNodeTypes]}
            </div>

            <div className="flex justify-between mt-2 items-center">
              <div className="flex min-w-0 items-center gap-1.5">
                <div className="font-jetbrains text-label uppercase tracking-normal text-tertiary">
                  {smart ? 'Smart conversation' : meta.name}
                </div>
                {type === FlowNodeTypes.ai_agent_v29 && (
                  <Badge
                    variant="default"
                    className="shrink-0 px-1.5 py-0 text-tertiary text-xs-600 leading-4"
                  >
                    Beta
                  </Badge>
                )}
              </div>
              {meta.issues && meta.issues.length > 0 && (
                <Tooltip
                  content={formatValidationErrors(meta.issues)}
                  delayDuration={300}
                  className="whitespace-pre-line"
                >
                  <div>
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <NodeOutputHandles id={id} node={node} />
        </div>
        <EvalBadge
          evalStatus={evalStatus}
          evalPassedCount={evalPassedCount}
          evalTotalCount={evalTotalCount}
          id={id}
        />
      </div>
    </div>
  )
}
